import { __ } from '@wordpress/i18n';

const MOBILE_BREAKPOINT = '(max-width: 767px)';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/**
 * Create accordion icon SVG.
 *
 * @param {"expanded"|"collapsed"} state Accordion state.
 * @return {SVGElement} SVG icon.
 */
const createAccordionIcon = ( state ) => {

	const svg = document.createElementNS( SVG_NAMESPACE, 'svg' );
	const path = document.createElementNS( SVG_NAMESPACE, 'path' );

	if ( state === 'expanded' ) {

		svg.setAttribute( 'width', '16' );
		svg.setAttribute( 'height', '2' );
		svg.setAttribute( 'viewBox', '0 0 16 2' );
		svg.setAttribute( 'fill', 'none' );
		path.setAttribute( 'd', 'M1 1H15' );
		path.setAttribute( 'stroke', '#0B62D6' );
		path.setAttribute( 'stroke-width', '2' );
		path.setAttribute( 'stroke-linecap', 'round' );
		path.setAttribute( 'stroke-linejoin', 'round' );

	} else {

		svg.setAttribute( 'width', '24' );
		svg.setAttribute( 'height', '24' );
		svg.setAttribute( 'viewBox', '0 0 24 24' );
		svg.setAttribute( 'fill', 'none' );
		path.setAttribute( 'd', 'M12 4C12.2652 4 12.5196 4.10536 12.7071 4.29289C12.8946 4.48043 13 4.73478 13 5V11H19C19.2652 11 19.5196 11.1054 19.7071 11.2929C19.8946 11.4804 20 11.7348 20 12C20 12.2652 19.8946 12.5196 19.7071 12.7071C19.5196 12.8946 19.2652 13 19 13H13V19C13 19.2652 12.8946 19.5196 12.7071 19.7071C12.5196 19.8946 12.2652 20 12 20C11.7348 20 11.4804 19.8946 11.2929 19.7071C11.1054 19.5196 11 19.2652 11 19V13H5C4.73478 13 4.48043 12.8946 4.29289 12.7071C4.10536 12.5196 4 12.2652 4 12C4 11.7348 4.10536 11.4804 4.29289 11.2929C4.48043 11.1054 4.73478 11 5 11H11V5C11 4.73478 11.1054 4.48043 11.2929 4.29289C11.4804 4.10536 11.7348 4 12 4Z' );
		path.setAttribute( 'fill', '#6FA3F5' );

	}

	svg.setAttribute( 'xmlns', SVG_NAMESPACE );
	svg.setAttribute( 'aria-hidden', 'true' );
	path.setAttribute( 'vector-effect', 'non-scaling-stroke' );
	svg.appendChild( path );

	return svg;

};

/**
 * Replace accordion icon content with state-specific SVG.
 *
 * @param {HTMLElement} iconWrapper Icon wrapper element.
 * @param {boolean} isExpanded Whether section is expanded.
 * @return {void}
 */
const setAccordionIcon = ( iconWrapper, isExpanded ) => {

	iconWrapper.replaceChildren( createAccordionIcon( isExpanded ? 'expanded' : 'collapsed' ) );

};

/**
 * Detect mobile row content type from the active value cell.
 *
 * @param {HTMLTableCellElement|null} cell Active value cell.
 * @return {"check"|"minus"|"text"|"empty"} Cell content type.
 */
const getMobileCellType = ( cell ) => {

	if ( ! cell ) {

		return 'empty';

	}

	if ( cell.querySelector( '.b-pricing-table__status--check' ) ) {

		return 'check';

	}

	if ( cell.querySelector( '.b-pricing-table__status--minus' ) ) {

		return 'minus';

	}

	const content = cell.querySelector( '.b-pricing-table__value-content' )?.textContent?.trim();

	return content ? 'text' : 'empty';

};

/**
 * Enable accordion arrow-key navigation.
 *
 * @param {HTMLButtonElement[]} triggers Accordion trigger buttons.
 * @param {KeyboardEvent} event Current keydown event.
 * @param {number} currentIndex Current trigger index.
 */
const handleArrowNavigation = ( triggers, event, currentIndex ) => {

	const keyMap = {
		ArrowDown: () => ( currentIndex + 1 ) % triggers.length,
		ArrowUp: () => ( currentIndex - 1 + triggers.length ) % triggers.length,
		Home: () => 0,
		End: () => triggers.length - 1,
	};

	if ( ! keyMap[ event.key ] ) {

		return;

	}

	event.preventDefault();
	triggers[ keyMap[ event.key ]() ].focus();

};

document.querySelectorAll( '.b-pricing-table' ).forEach( ( block, blockIndex ) => {

	const tableWrap = block.querySelector( '.b-pricing-table__table-wrap' );
	const table = tableWrap ? tableWrap.querySelector( '.b-pricing-table__table' ) : null;
	const cornerCell = table ? table.querySelector( 'thead .b-pricing-table__corner' ) : null;
	const headerCells = table ? Array.from( table.querySelectorAll( 'thead .b-pricing-table__plan[data-column-id]' ) ) : [];
	const sectionBodies = table ? Array.from( table.querySelectorAll( 'tbody.b-pricing-table__section' ) ) : [];

	if ( ! tableWrap || ! table || ! headerCells.length || ! sectionBodies.length ) {

		return;

	}

	const mediaQuery = window.matchMedia( MOBILE_BREAKPOINT );
	const idPrefix = `pricing-table-${ blockIndex }`;
	const tablist = document.createElement( 'div' );
	tablist.className = 'b-pricing-table__mobile-tablist';
	tablist.setAttribute( 'aria-label', block.dataset.tablistLabel || __( 'Choose pricing plan', 'wombat' ) );
	tablist.hidden = true;
	tableWrap.insertBefore( tablist, table );

	let activeColumnId = headerCells[ 0 ].dataset.columnId;
	let activeSectionIndex = 0;

	const plans = headerCells.map( ( cell, index ) => {

		const button = document.createElement( 'button' );
		button.type = 'button';
		button.className = 'b-pricing-table__mobile-tab';
		button.textContent = cell.querySelector( '.b-pricing-table__plan-title' )?.textContent?.trim() || `${ __( 'Plan', 'wombat' ) } ${ index + 1 }`;
		button.dataset.columnId = cell.dataset.columnId;
		button.setAttribute( 'aria-pressed', 'false' );

		const variantClass = Array.from( cell.classList ).find( ( className ) => className.startsWith( 'b-pricing-table__plan--' ) );
		if ( variantClass ) {

			button.classList.add( variantClass );

		}

		tablist.appendChild( button );

		return {
			button,
			cell,
			columnId: cell.dataset.columnId,
		};

	} );

	const sections = sectionBodies.map( ( tbody, sectionIndex ) => {

		const rows = Array.from( tbody.querySelectorAll( 'tr' ) );
		const sectionRow = rows[ 0 ];
		const contentRows = rows.slice( 1 );
		const inner = sectionRow?.querySelector( '.b-pricing-table__section-cell-inner' );
		const copy = sectionRow?.querySelector( '.b-pricing-table__section-copy' );
		const button = document.createElement( 'button' );
		button.type = 'button';
		button.className = 'b-pricing-table__section-toggle';
		button.id = `${ idPrefix }-section-toggle-${ sectionIndex }`;
		button.setAttribute( 'aria-expanded', sectionIndex === 0 ? 'true' : 'false' );

		const buttonCopy = document.createElement( 'span' );
		buttonCopy.className = 'b-pricing-table__section-toggle-copy';
		if ( copy ) {

			buttonCopy.appendChild( copy );

		}

		const icon = document.createElement( 'span' );
		icon.className = 'b-pricing-table__section-toggle-icon';
		icon.setAttribute( 'aria-hidden', 'true' );
		setAccordionIcon( icon, sectionIndex === 0 );

		button.append( buttonCopy, icon );

		if ( inner ) {

			inner.appendChild( button );

		}

		return {
			tbody,
			button,
			icon,
			isDesktopExpanded: true,
			rows: contentRows,
			rowItems: contentRows.map( ( row ) => ( {
				row,
				valueCells: Array.from( row.querySelectorAll( '.b-pricing-table__value[data-column-id]' ) ),
			} ) ),
		};

	} );

	/**
	 * Sync one mobile row against the active column.
	 *
	 * @param {Object} rowItem Row metadata.
	 */
	const syncMobileRow = ( rowItem ) => {

		const activeCell = rowItem.valueCells.find( ( cell ) => cell.dataset.columnId === activeColumnId ) || null;
		const cellType = getMobileCellType( activeCell );
		const shouldShow = cellType !== 'minus' && cellType !== 'empty';

		rowItem.row.classList.remove(
			'is-mobile-check',
			'is-mobile-minus',
			'is-mobile-text',
			'is-mobile-empty'
		);
		rowItem.row.classList.add( `is-mobile-${ cellType }` );
		rowItem.row.hidden = mediaQuery.matches ? ! shouldShow : false;

	};

	/**
	 * Set active mobile plan.
	 *
	 * @param {string} columnId Column id to show.
	 * @param {boolean} focusButton Whether to focus selected button.
	 */
	const setActivePlan = ( columnId, focusButton ) => {

		activeColumnId = columnId;

		plans.forEach( ( plan ) => {

			const isActive = plan.columnId === columnId;
			plan.button.setAttribute( 'aria-pressed', String( isActive ) );
			plan.button.classList.toggle( 'is-active', isActive );
			plan.cell.hidden = mediaQuery.matches ? ! isActive : false;
			plan.cell.classList.toggle( 'is-mobile-active', isActive );

			if ( focusButton && isActive ) {

				plan.button.focus();

			}

		} );

		sectionBodies.forEach( ( tbody ) => {

			Array.from( tbody.querySelectorAll( '.b-pricing-table__value[data-column-id]' ) ).forEach( ( cell ) => {

				const isActive = cell.dataset.columnId === columnId;
				cell.hidden = mediaQuery.matches ? ! isActive : false;
				cell.classList.toggle( 'is-mobile-active', isActive );

			} );

		} );

		sections.forEach( ( section ) => {

			section.rowItems.forEach( ( rowItem ) => syncMobileRow( rowItem ) );

		} );

	};

	/**
	 * Sync one desktop section against its expanded state.
	 *
	 * @param {Object} section Section metadata.
	 * @return {void}
	 */
	const syncDesktopSection = ( section ) => {

		section.tbody.classList.toggle( 'is-desktop-collapsed', ! section.isDesktopExpanded );
		section.button.setAttribute( 'aria-expanded', String( section.isDesktopExpanded ) );
		setAccordionIcon( section.icon, section.isDesktopExpanded );

	};

	/**
	 * Set expanded mobile section.
	 *
	 * @param {number} sectionIndex Section index.
	 * @param {boolean} [forceExpand] Explicit next state.
	 */
	const toggleSection = ( sectionIndex, forceExpand ) => {

		activeSectionIndex = sectionIndex;

		sections.forEach( ( section, index ) => {

			const isCurrentlyExpanded = section.button.getAttribute( 'aria-expanded' ) === 'true';
			let shouldExpand = false;

			if ( index === sectionIndex ) {

				shouldExpand = typeof forceExpand === 'boolean' ? forceExpand : ! isCurrentlyExpanded;

			}

			section.tbody.classList.toggle( 'is-mobile-expanded', shouldExpand );
			section.button.setAttribute( 'aria-expanded', String( shouldExpand ) );
			setAccordionIcon( section.icon, shouldExpand );
			section.rowItems.forEach( ( rowItem ) => syncMobileRow( rowItem ) );

		} );

	};

	plans.forEach( ( plan, index ) => {

		plan.button.addEventListener( 'click', () => setActivePlan( plan.columnId, false ) );
		plan.button.addEventListener( 'keydown', ( event ) => {

			const keyMap = {
				ArrowRight: () => plans[ ( index + 1 ) % plans.length ].columnId,
				ArrowLeft: () => plans[ ( index - 1 + plans.length ) % plans.length ].columnId,
				Home: () => plans[ 0 ].columnId,
				End: () => plans[ plans.length - 1 ].columnId,
			};

			if ( ! keyMap[ event.key ] ) {

				return;

			}

			event.preventDefault();
			setActivePlan( keyMap[ event.key ](), true );

		} );

	} );

	sections.forEach( ( section, index ) => {

		section.button.addEventListener( 'click', () => {

			if ( ! mediaQuery.matches ) {

				section.isDesktopExpanded = ! section.isDesktopExpanded;
				syncDesktopSection( section );
				return;

			}

			toggleSection( index );

		} );

		section.button.addEventListener( 'keydown', ( event ) => {

			handleArrowNavigation( sections.map( ( item ) => item.button ), event, index );

			if ( event.key === 'Escape' ) {

				event.preventDefault();
				if ( mediaQuery.matches ) {

					toggleSection( index, false );

				} else {

					section.isDesktopExpanded = false;
					syncDesktopSection( section );

				}
				section.button.focus();

			}

		} );

	} );

	/**
	 * Sync DOM state between mobile and desktop layouts.
	 */
	const syncResponsiveMode = () => {

		const isMobile = mediaQuery.matches;
		tablist.hidden = ! isMobile;
		block.classList.toggle( 'b-pricing-table--mobile-enhanced', isMobile );

		if ( cornerCell ) {

			cornerCell.hidden = isMobile;

		}

		if ( ! isMobile ) {

			plans.forEach( ( plan ) => {

				plan.cell.hidden = false;
				plan.cell.classList.remove( 'is-mobile-active' );
				plan.button.setAttribute( 'aria-pressed', String( plan.columnId === activeColumnId ) );
				plan.button.classList.toggle( 'is-active', plan.columnId === activeColumnId );

			} );

			sections.forEach( ( section ) => {

				section.rowItems.forEach( ( rowItem ) => {

					rowItem.valueCells.forEach( ( cell ) => {

						cell.hidden = false;
						cell.classList.remove( 'is-mobile-active' );

					} );

				} );

				section.tbody.classList.remove( 'is-mobile-expanded' );
				section.rows.forEach( ( row ) => {

					row.hidden = false;

				} );
				syncDesktopSection( section );

			} );

			return;

		}

		setActivePlan( activeColumnId, false );
		toggleSection( activeSectionIndex, true );

	};

	setActivePlan( activeColumnId, false );
	sections.forEach( ( section ) => syncDesktopSection( section ) );
	toggleSection( 0, true );
	syncResponsiveMode();

	if ( typeof mediaQuery.addEventListener === 'function' ) {

		mediaQuery.addEventListener( 'change', syncResponsiveMode );

	} else if ( typeof mediaQuery.addListener === 'function' ) {

		mediaQuery.addListener( syncResponsiveMode );

	}

} );
